import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonIcon, IonSpinner } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { alertCircleOutline, archiveOutline, arrowUpOutline, documentAttachOutline } from 'ionicons/icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChatLegacyThread } from '../../models/agents.models';
import { AgentsApiService } from '../../services/agents/agents-api.service';
import { DateFormatService } from '../../services/date-format.service';
import { TranslationParams, TranslationService } from '../../services/translation.service';
import { AgentsMarkdownComponent } from '../agents/agents-markdown/agents-markdown.component';

/** One shown item of the old chat, with what the template needs computed once. */
export interface LegacyItemView {
  key: string;
  kind: 'user' | 'assistant' | 'card' | 'file';
  text: string;
  fileName: string;
  when: string;
}

/**
 * Words typed under the old chat, for Solo. The host calls `resolve(true)` once they were sent to Solo's thread (the box
 * is then cleared) and `resolve(false)` when they were not (the words stay in the box).
 */
export interface LegacySendRequest {
  text: string;
  resolve(sent: boolean): void;
}

const AGENTS_NAMESPACE = 'agents';

/**
 * An old ChatKit chat, read only (Channels C0b P5, C0B-DESIGN 3.6): its user and assistant bubbles (Markdown), its
 * widget cards as text, and its attachments' names (old files cannot be downloaded: their blobs belong to the retired
 * customer-chat service). A banner says it is read only and that Solo does not see it; the composer under it sends a
 * message to Solo (the host opens Solo's thread and sends it there). Nothing here can continue, rename or delete it.
 */
@Component({
  selector: 'app-legacy-thread-view',
  standalone: true,
  imports: [FormsModule, IonIcon, IonSpinner, AgentsMarkdownComponent],
  templateUrl: './legacy-thread-view.component.html',
  styleUrls: ['./legacy-thread-view.component.scss']
})
export class LegacyThreadViewComponent implements OnInit, OnChanges, OnDestroy {
  /** The old chat to show. */
  @Input() threadId: string | null = null;
  /** The person typed a message under the old chat: it goes to Solo, not to this chat (see LegacySendRequest). */
  @Output() readonly sendToSolo = new EventEmitter<LegacySendRequest>();

  loading = false;
  /** The chat cannot be shown (missing, or not the caller's: the API gives both the same answer). */
  unavailable = false;
  failed = false;
  title = '';
  items: LegacyItemView[] = [];
  hiddenText = '';
  truncated = false;
  draft = '';
  /** Words are on their way to Solo: the box keeps them until the host says they were sent. */
  sending = false;

  private thread: ChatLegacyThread | null = null;
  private requestSeq = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly api: AgentsApiService,
    private readonly translations: TranslationService,
    private readonly dates: DateFormatService
  ) {
    addIcons({ alertCircleOutline, archiveOutline, arrowUpOutline, documentAttachOutline });
  }

  ngOnInit(): void {
    this.translations.loadNamespace(AGENTS_NAMESPACE).pipe(takeUntil(this.destroy$)).subscribe(() => this.rebuild());
    void this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['threadId'] && !changes['threadId'].firstChange) void this.load();
  }

  ngOnDestroy(): void {
    this.requestSeq++;
    this.destroy$.next();
    this.destroy$.complete();
  }

  t(key: string, params?: TranslationParams): string {
    return this.translations.getTranslation(key, params, { namespace: AGENTS_NAMESPACE });
  }

  async load(): Promise<void> {
    const threadId = this.threadId;
    const seq = ++this.requestSeq;
    this.thread = null;
    this.unavailable = false;
    this.failed = false;
    if (!threadId) {
      this.rebuild();
      return;
    }
    this.loading = true;
    this.rebuild();
    const result = await this.api.chatLegacyGet(threadId);
    if (seq !== this.requestSeq) return;
    this.loading = false;
    if (result.ok) this.thread = result.data;
    else if (result.code === 'thread_not_found' || result.status === 404) this.unavailable = true;
    else this.failed = true;
    this.rebuild();
  }

  send(): void {
    const text = this.draft.trim();
    if (!text || this.sending) return;
    this.sending = true;
    this.sendToSolo.emit({
      text,
      resolve: sent => {
        this.sending = false;
        // Cleared only when these words went; anything typed since stays.
        if (sent && this.draft.trim() === text) this.draft = '';
      }
    });
  }

  onKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  /** Recomputes everything the template reads. Called once per change, never from the template. */
  private rebuild(): void {
    const thread = this.thread;
    this.title = thread?.title?.trim() || this.t('agents.solo_chat.legacy.untitled');
    this.truncated = !!thread?.truncated;
    this.hiddenText = thread && thread.hiddenCount > 0 ? this.t('agents.solo_chat.legacy.hidden', { count: thread.hiddenCount }) : '';
    this.items = (thread?.items ?? []).flatMap((item, index): LegacyItemView[] => {
      const kind = item.kind === 'user' || item.kind === 'assistant' || item.kind === 'card' || item.kind === 'file' ? item.kind : null;
      if (!kind) return [];
      if (kind === 'file' ? !item.fileName : !item.text) return [];
      return [{
        key: `${index}`,
        kind,
        text: item.text ?? '',
        fileName: item.fileName ?? '',
        when: item.createdOn ? this.dates.formatDateTime(item.createdOn) : ''
      }];
    });
  }
}
